import React from 'react';
import PublicHeader from './PublicHeader';

interface FishingRegulationsPageProps {
  onNavigateHome: () => void;
  onMemberNavigate?: (page: 'dashboard' | 'profile' | 'competitions' | 'catches' | 'my-stats' | 'my-fishing' | 'season-championship' | 'home') => void;
}

const LINKS = [
  { label: 'NSW DPI Recreational Fishing', url: 'https://www.dpi.nsw.gov.au/fishing/recreational' },
  { label: 'Buy a Fishing Licence (Service NSW)', url: 'https://www.service.nsw.gov.au/transaction/buy-a-recreational-fishing-fee-licence' },
  { label: 'NSW Fishing App (DPI)', url: 'https://www.dpi.nsw.gov.au/fishing/recreational/resources/nsw-fishing-app' },
  { label: 'Tweed River Fishing Info', url: 'https://www.dpi.nsw.gov.au/fishing/recreational/fishing-in-nsw/where-to-fish/tweed-river' },
];

const FishingRegulationsPage: React.FC<FishingRegulationsPageProps> = ({ onNavigateHome, onMemberNavigate }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PublicHeader title="NSW Fishing Regulations" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">NSW Fishing Regulations</h1>
        <p className="text-lg text-gray-600 mb-6">Tweed River &amp; Tumbulgum Area — Key rules for recreational fishing</p>

        {/* Legal Sizes — link to DPI */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">🐟 Legal Size &amp; Bag Limits</h2>
          <p className="text-sm text-gray-700 mb-4">
            For current legal sizes, bag limits, and possession limits for all species in NSW waters, refer directly to the NSW DPI website — regulations change and the DPI site is always up to date.
          </p>
          <a
            href="https://www.dpi.nsw.gov.au/fishing/recreational/fishing-rules-and-regulations/size-and-bag-limits"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-fishing-blue bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
          >
            NSW DPI — Size &amp; Bag Limits ↗
          </a>
        </div>

        {/* General Rules */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">📋 General Rules</h2>
          <ul className="space-y-2 text-sm text-gray-700 list-disc list-inside">
            <li>A valid NSW recreational fishing licence is required for anyone aged 18+ fishing in NSW waters</li>
            <li>Licence fees fund fisheries management and habitat restoration</li>
            <li>Get your licence at Service NSW or <a href="https://www.service.nsw.gov.au" target="_blank" rel="noopener noreferrer" className="text-fishing-blue hover:underline">service.nsw.gov.au</a></li>
            <li>Bag limits apply per person per day</li>
            <li>Undersized fish must be returned to the water immediately and carefully</li>
          </ul>
        </div>

        {/* Tweed River Specific */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">🌊 Tweed River Specific Notes</h2>
          <ul className="space-y-2 text-sm text-gray-700 list-disc list-inside">
            <li>The Tweed River is a tidal estuary — a saltwater recreational fishing licence applies</li>
            <li>Check for current closures or special conditions on the NSW DPI website</li>
            <li>Respect riparian landowners — access points are limited in some areas</li>
          </ul>
        </div>

        {/* Useful Links */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">🔗 Useful Links</h2>
          <div className="flex flex-wrap gap-3">
            {LINKS.map((link) => (
              <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-4 py-2 text-sm font-medium text-fishing-blue bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100">
                {link.label} ↗
              </a>
            ))}
          </div>
        </div>

        {/* Club Competition Rules */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">🏆 Club Competition Rules — Quick Reference</h2>
          <ul className="space-y-2 text-sm text-gray-700 list-disc list-inside">
            <li>Minimum sizes are enforced by the app — undersized kept catches are rejected</li>
            <li>Released undersized fish are accepted and recorded</li>
            <li>Species eligible for competition: Flathead, Bream, Jewfish, Mangrove Jack, Whiting, Tailor, Trevally, Other</li>
            <li>"Other" species are subject to NSW DPI size limits but may not be eligible for competition prizes — check with committee.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FishingRegulationsPage;
